/**
 * API client factory (04-architecture § 8.1).
 *
 * Types come from `@cowoo/api-client`, which the server publishes from its
 * OpenAPI document — this repo never hand-writes a request or response type.
 */

import createClient, { type Client, type Middleware } from 'openapi-fetch';
import type { paths } from '@cowoo/api-client';
import { ApiProblem } from './problem.js';

export type ApiClient = Client<paths>;

export interface ApiClientOptions {
  /** API origin, e.g. `https://api.cowoo.de`; `/v1` is appended here. */
  baseUrl: string;
  /** Synchronous read of the OtterAuth session token from memory. */
  getToken: () => string | null;
  /** Called once on `401 session_invalid` (§ 8.3). */
  onUnauthorized: () => void;
  /** Mobile passes `fetch` from `expo/fetch` for streaming parity. */
  fetch?: typeof globalThis.fetch;
  appInfo: {
    app: 'mobile' | 'site' | 'member' | 'admin';
    version: string;
    platform?: 'ios' | 'android' | 'web';
  };
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const client = createClient<paths>({
    baseUrl: `${options.baseUrl.replace(/\/$/, '')}/v1`,
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });

  const auth: Middleware = {
    onRequest({ request }) {
      const token = options.getToken();
      if (token) request.headers.set('Authorization', `Bearer ${token}`);
      request.headers.set('Accept', 'application/json, application/problem+json');
      // No other custom headers: the API's CORS allow-list is exactly
      // authorization, content-type and idempotency-key. Mobile identifies
      // itself through the User-Agent set in its fetch wrapper.
      return request;
    },
    async onResponse({ response }) {
      // Nothing returned = the response passes untouched. Handing it back
      // instead would make openapi-fetch check `instanceof Response`, which
      // `expo/fetch`'s native `FetchResponse` is not — every 200 on the phone
      // then throws "onResponse: must return new Response()".
      if (response.ok) return undefined;
      const problem = await ApiProblem.fromResponse(response);
      if (response.status === 401 && problem.code === 'session_invalid') options.onUnauthorized();
      throw problem;
    },
  };

  client.use(auth);
  return client;
}

/** A fresh `Idempotency-Key` for a ⟲ endpoint (04 § 8.5). */
export function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}

/** Request timeout of 04 § 10.3: 20 s normally, 120 s for uploads. */
export const REQUEST_TIMEOUT_MS = 20_000;
export const UPLOAD_TIMEOUT_MS = 120_000;

export function withTimeout(ms = REQUEST_TIMEOUT_MS): AbortSignal {
  return AbortSignal.timeout(ms);
}
